import { useCallback, useEffect, useReducer, useRef } from 'react';

import { useAuth } from '@opendoor/auth-fe/context';
import { MarketplacePropertyCategory_ReactionEventReaction } from '@opendoor/observability/pbs/journey/data/categories/marketplace_property_category_pb';
import { requestZeusFromBrowser } from 'components/api/zeusBrowserClient';
import { useTrackProperty } from 'components/marketplace/helpers/useTrackProperty';
import { useRouter } from 'next/router';

import { useObservability } from '../../../helpers/observability';
import {
  type AddMarketplaceFavoriteInput,
  MarketplaceAddFavoriteDocument,
  type MarketplaceFavoriteOrigin,
  MarketplaceListingFavoriteStateDocument,
  MarketplaceRemoveFavoriteDocument,
  type RemoveMarketplaceFavoriteInput,
} from './__generated__/marketplace-favorites.zeus';

const redirectToLogin = (addressId: string) => {
  const url = new URL(window.location.toString());
  url.searchParams.set('likedId', addressId);
  window.location.assign(`/login?redirectTo=${encodeURIComponent(url.toString())}`);
};

type FavoriteState = {
  addressId?: string | null;
  isLiked: boolean;
  isKnown: boolean;
  isMutating: boolean;
};

type FavoriteAction =
  | {
      type: 'RESET';
      payload: { addressId?: string | null; isKnown: boolean; isLiked: boolean };
    }
  | {
      type: 'START_TOGGLE_FAVORITE';
      payload: { addressId: string };
    }
  | {
      type: 'SET_FAVORITE';
      payload: { addressId: string; isLiked: boolean };
    };

type FavoriteMutationResult = {
  isLiked: boolean;
  succeeded: boolean;
};

type FavoriteToggleAction = {
  analyticsReaction: MarketplacePropertyCategory_ReactionEventReaction;
  mutate: () => Promise<FavoriteMutationResult>;
  trackingLabel: 'add-favorite' | 'remove-favorite';
};

const favoriteReducer = (state: FavoriteState, action: FavoriteAction): FavoriteState => {
  switch (action.type) {
    case 'RESET':
      return {
        addressId: action.payload.addressId,
        isKnown: action.payload.isKnown,
        isLiked: action.payload.isLiked,
        isMutating: false,
      };
    case 'START_TOGGLE_FAVORITE':
      if (state.addressId !== action.payload.addressId) {
        return state;
      }
      return { ...state, isKnown: true, isLiked: !state.isLiked, isMutating: true };
    case 'SET_FAVORITE':
      if (state.addressId !== action.payload.addressId) {
        return state;
      }
      return { ...state, isKnown: true, isLiked: action.payload.isLiked, isMutating: false };
  }
  action satisfies never;
  return state;
};

const favoriteAnalyticsSource = {
  GALLERY: 'REACTION_SOURCE_GALLERY',
  LISTING_DETAILS: 'REACTION_SOURCE_WEB_PDP',
} as const satisfies Record<MarketplaceFavoriteOrigin, string>;

const checkIfLiked = async (addressId: string) => {
  const result = await requestZeusFromBrowser(MarketplaceListingFavoriteStateDocument, {
    addressId,
  });
  return result.marketplace.listing?.isLiked ?? false;
};

const likeProperty = async (
  input: AddMarketplaceFavoriteInput,
): Promise<FavoriteMutationResult> => {
  try {
    const result = await requestZeusFromBrowser(MarketplaceAddFavoriteDocument, {
      input,
    });
    const succeeded =
      result.addMarketplaceFavorite.__typename === 'CommandResult' &&
      result.addMarketplaceFavorite.success;
    return { isLiked: succeeded, succeeded };
  } catch {
    return { isLiked: false, succeeded: false };
  }
};

const unlikeProperty = async (
  input: RemoveMarketplaceFavoriteInput,
): Promise<FavoriteMutationResult> => {
  try {
    const result = await requestZeusFromBrowser(MarketplaceRemoveFavoriteDocument, {
      input,
    });
    const succeeded =
      result.removeMarketplaceFavorite.__typename === 'CommandResult' &&
      result.removeMarketplaceFavorite.success;
    return { isLiked: !succeeded, succeeded };
  } catch {
    return { isLiked: true, succeeded: false };
  }
};

type UseMarketplaceFavoriteOptions = {
  addressId?: string | null;
  origin?: MarketplaceFavoriteOrigin;
  initialIsLiked?: boolean | null;
  initialFavoritePending?: boolean;
  onFavoriteChange?: MarketplaceFavoriteChangeHandler;
};

export type MarketplaceFavoriteChangeHandler = (
  addressId: string,
  isLiked: boolean,
) => Promise<void>;

export const useMarketplaceFavorite = ({
  addressId,
  origin = 'LISTING_DETAILS',
  initialIsLiked,
  initialFavoritePending = false,
  onFavoriteChange,
}: UseMarketplaceFavoriteOptions) => {
  const { authentication } = useAuth();
  const router = useRouter();
  const isLoggedIn = authentication.state === 'authenticated';
  const { trackProperty } = useTrackProperty();
  const { trackJourneyEvent } = useObservability();
  const likedIdExists = router.query['likedId'] === addressId;
  const analyticsSource = favoriteAnalyticsSource[origin];
  const latestReadId = useRef(0);
  const pendingMutationAddressIds = useRef(new Set<string>());
  const [state, dispatch] = useReducer(favoriteReducer, {
    addressId,
    isKnown: initialIsLiked != null || !isLoggedIn,
    isLiked: initialIsLiked ?? false,
    isMutating: false,
  });
  const isFavoriteLoading =
    isLoggedIn && (state.addressId !== addressId || !state.isKnown || state.isMutating);

  const onFinishLikedId = useCallback(
    (likedAddressId: string) => {
      if (router.query['likedId'] !== likedAddressId) {
        return;
      }
      const { likedId: _, ...query } = router.query;
      void router.replace(
        {
          query,
        },
        undefined,
        { shallow: true },
      );
    },
    [router],
  );

  useEffect(() => {
    latestReadId.current += 1;
    dispatch({
      type: 'RESET',
      payload: {
        addressId,
        isKnown: initialIsLiked != null || !isLoggedIn,
        isLiked: initialIsLiked ?? false,
      },
    });
  }, [addressId, initialIsLiked, isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn || !addressId || !likedIdExists) {
      return undefined;
    }

    const favoriteAddressId = addressId;
    const readId = (latestReadId.current += 1);
    let cancelled = false;
    pendingMutationAddressIds.current.add(favoriteAddressId);
    dispatch({ type: 'START_TOGGLE_FAVORITE', payload: { addressId: favoriteAddressId } });

    async function replayFavoriteAfterLogin() {
      try {
        const result = await likeProperty({ addressId: favoriteAddressId, origin });
        if (!cancelled && readId === latestReadId.current) {
          dispatch({
            type: 'SET_FAVORITE',
            payload: { addressId: favoriteAddressId, isLiked: result.isLiked },
          });
          if (result.succeeded) {
            await onFavoriteChange?.(favoriteAddressId, result.isLiked);
          }
        }
      } finally {
        pendingMutationAddressIds.current.delete(favoriteAddressId);
        onFinishLikedId(favoriteAddressId);
      }
    }

    void replayFavoriteAfterLogin();
    return () => {
      cancelled = true;
    };
  }, [addressId, isLoggedIn, likedIdExists, onFavoriteChange, onFinishLikedId, origin]);

  useEffect(() => {
    if (
      !isLoggedIn ||
      !addressId ||
      likedIdExists ||
      initialIsLiked != null ||
      initialFavoritePending
    ) {
      return undefined;
    }

    const favoriteAddressId = addressId;
    const readId = (latestReadId.current += 1);
    let cancelled = false;

    async function hydrateFavoriteState() {
      try {
        const isLiked = await checkIfLiked(favoriteAddressId);
        if (!cancelled && readId === latestReadId.current) {
          dispatch({ type: 'SET_FAVORITE', payload: { addressId: favoriteAddressId, isLiked } });
        }
      } catch {
        if (!cancelled && readId === latestReadId.current) {
          dispatch({
            type: 'SET_FAVORITE',
            payload: { addressId: favoriteAddressId, isLiked: false },
          });
        }
      }
    }

    void hydrateFavoriteState();
    return () => {
      cancelled = true;
    };
  }, [addressId, initialFavoritePending, initialIsLiked, isLoggedIn, likedIdExists]);

  const onToggleFavorite = useCallback(() => {
    if (!addressId) {
      return;
    }
    if (!isLoggedIn) {
      trackProperty('property-favorite', 'redirect-to-login');
      return redirectToLogin(addressId);
    }
    if (isFavoriteLoading) {
      return;
    }
    if (pendingMutationAddressIds.current.has(addressId)) {
      return;
    }

    const favoriteAddressId = addressId;
    pendingMutationAddressIds.current.add(favoriteAddressId);
    latestReadId.current += 1;
    const favoriteAction: FavoriteToggleAction = state.isLiked
      ? {
          analyticsReaction: MarketplacePropertyCategory_ReactionEventReaction.REACTION_REMOVE_LIKE,
          mutate: () => unlikeProperty({ addressId: favoriteAddressId }),
          trackingLabel: 'remove-favorite',
        }
      : {
          analyticsReaction: MarketplacePropertyCategory_ReactionEventReaction.REACTION_ADD_LIKE,
          mutate: () => likeProperty({ addressId: favoriteAddressId, origin }),
          trackingLabel: 'add-favorite',
        };
    dispatch({
      type: 'START_TOGGLE_FAVORITE',
      payload: { addressId: favoriteAddressId },
    });

    async function persistFavoriteToggle() {
      try {
        const result = await favoriteAction.mutate();
        dispatch({
          type: 'SET_FAVORITE',
          payload: { addressId: favoriteAddressId, isLiked: result.isLiked },
        });
        if (!result.succeeded) {
          return;
        }
        await onFavoriteChange?.(favoriteAddressId, result.isLiked);
        trackProperty('property-favorite', favoriteAction.trackingLabel);
        trackJourneyEvent('marketplaceProperty', 'reaction', {
          addressUuid: favoriteAddressId,
          reaction: favoriteAction.analyticsReaction,
          source: analyticsSource,
        });
      } finally {
        pendingMutationAddressIds.current.delete(favoriteAddressId);
      }
    }

    void persistFavoriteToggle();
  }, [
    addressId,
    analyticsSource,
    isLoggedIn,
    isFavoriteLoading,
    onFavoriteChange,
    origin,
    state.isLiked,
    trackJourneyEvent,
    trackProperty,
  ]);

  return {
    onToggleFavorite,
    isFavoriteLoading,
    isLiked: state.addressId === addressId ? state.isLiked : (initialIsLiked ?? false),
  };
};
